import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { can } from './lib/permissions';
import { canViewAgreements } from './utils/agreements';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import CalendarioPage from './pages/CalendarioPage';
import InformesPage from './pages/InformesPage';
import EquipoPage from './pages/EquipoPage';
import EmpresasPage from './pages/EmpresasPage';
import CompanyDetailPage from './pages/CompanyDetailPage';
import WorkerDetailPage from './pages/WorkerDetailPage';
import CompararCalendariosPage from './pages/CompararCalendariosPage';
import AprobacionesPage from './pages/AprobacionesPage';
import ConveniosPage from './pages/ConveniosPage';
import FestivosPage from './pages/FestivosPage';
import DocumentosPage from './pages/DocumentosPage';
import PanelLayout from './components/PanelLayout';
import { ToastProvider } from './components/Toast';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Gate de la raíz "/":
//   · Primera visita en este navegador → muestra HomePage (marketing) y
//     marca el flag para que las próximas siempre vayan a /login.
//   · Visitas posteriores → redirige directamente a /login.
//   · Si el usuario ya tiene sesión activa, va a /panel sin ver ninguna de
//     las dos pantallas (el navigate dentro de LoginPage también lo
//     gestiona, pero adelantamos aquí para evitar el flash de login).
// El HomePage sigue accesible siempre vía /inicio.
const ROOT_VISITED_KEY = 'temcontrol_visited';

const RootGate = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/panel" replace />;
  let visited = false;
  try {
    visited = localStorage.getItem(ROOT_VISITED_KEY) === '1';
  } catch (_) {
    // localStorage puede fallar en modo incógnito muy estricto o si el
    // usuario lo bloqueó. En ese caso degradamos al comportamiento "siempre
    // primera visita" — ven HomePage cada vez. Mejor que romper la app.
  }
  if (visited) return <Navigate to="/login" replace />;
  try { localStorage.setItem(ROOT_VISITED_KEY, '1'); } catch (_) {}
  return <HomePage />;
};

// `allow(userType)` sustituye a `permission` para reglas que solo existen en la
// web (p.ej. convenios) y no deben ir a src/lib/permissions.js, que es
// compartido con la app.
const PermissionRoute = ({ permission, allow, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = allow ? allow(user.type) : can(user.type, permission);
  if (!allowed) return <Navigate to="/panel" replace />;
  return children;
};

// Para admin empresa y admin sede, /panel/empresas redirige automáticamente
// a su propia ficha de empresa (/panel/empresas/{companyId}). El resto pasa
// al gate normal de viewEmpresas.
const EmpresasIndexGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.type === 1 || user.type === 2) {
    if (user.companyId != null) {
      return <Navigate to={`/panel/empresas/${user.companyId}`} replace />;
    }
    return <Navigate to="/panel" replace />;
  }
  if (!can(user.type, 'viewEmpresas')) return <Navigate to="/panel" replace />;
  return children;
};

// La vista de detalle (/panel/empresas/:id) la ven los cuatro roles autorizados;
// el filtrado por scope lo hace CompanyDetailPage. Aquí sólo bloqueamos a quien
// no tiene viewOwnCompany (trabajador, supervisor).
const CompanyDetailGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(user.type, 'viewOwnCompany')) return <Navigate to="/panel" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/inicio" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/panel"
        element={
          <ProtectedRoute>
            <PanelLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="calendario" element={<CalendarioPage />} />
        <Route path="informes" element={<PermissionRoute permission="viewReports"><InformesPage /></PermissionRoute>} />
        <Route path="equipo" element={<PermissionRoute permission="viewEquipo"><EquipoPage /></PermissionRoute>} />
        <Route path="equipo/comparar" element={<PermissionRoute permission="viewEquipo"><CompararCalendariosPage /></PermissionRoute>} />
        <Route path="equipo/:id" element={<PermissionRoute permission="viewEquipo"><WorkerDetailPage /></PermissionRoute>} />
        <Route path="aprobaciones" element={<PermissionRoute permission="approveEvents"><AprobacionesPage /></PermissionRoute>} />
        <Route path="empresas" element={<EmpresasIndexGate><EmpresasPage /></EmpresasIndexGate>} />
        <Route path="empresas/:id" element={<CompanyDetailGate><CompanyDetailPage /></CompanyDetailGate>} />
        <Route path="convenios" element={<PermissionRoute allow={canViewAgreements}><ConveniosPage /></PermissionRoute>} />
        <Route path="festivos" element={<PermissionRoute permission="manageHolidays"><FestivosPage /></PermissionRoute>} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
